import { z } from 'zod';
import { FleetJobEndReasonSchema } from './fleet-jobs.schemas';
import { FleetQuestionAnswererKindSchema, FleetQuestionOptionSchema } from './fleet-questions.schemas';
import { FleetLaneNameSchema, FleetProjectNameSchema } from './fleet-status.schemas';

/**
 * The operator's board by fleet lane (OPS-69), the way `fleet-agent agent preview` shows one host: each lane's
 * rounds, the tickets stranded in its running state, its queue in the order `jobs/next` hands it out, and what it
 * does not take. The lanes are the ones workers reported when they started (`POST /v1/fleet/lanes`); the tickets come
 * from the same tracker snapshot the machines read, under the same 30s floor. The one write is a queue's manual
 * order (`PUT /v1/admin/fleet/board/rank`), which `jobs/next` honours too.
 */

/** `all`, or one project's name. */
export const FLEET_BOARD_ALL_VIEW = 'all';

export const FleetBoardQuerySchema = z.object({ view: z.string().min(1).max(64).default(FLEET_BOARD_ALL_VIEW) });
export type FleetBoardQuery = z.infer<typeof FleetBoardQuerySchema>;

/**
 * One tag per card, the preview's glyphs:
 *
 * - `running` 🤖 a live job holds it; `silent` 🤖 the same, with no heartbeat for `FLEET_JOB_STALE_AFTER_MS` or its
 *   round's own task log unwritten for `FLEET_JOB_LOG_SILENT_AFTER_MS` (fleet-agent ADR 0019).
 * - `stranded` ⚠️ in the lane's running state, but no job holds it.
 * - `free` 🟢 in the queue; `answered` 🟢 in the queue, its question answered since it parked — it goes first.
 * - `bound` 🔒 bound to one worker; `parked` 🔴 carries the parked label; `capped` 🪫 out of runs; `assisted` ✋ a
 *   person builds it by hand; `waiting` ⏳ carries the waiting label: a blocker has not merged (fleet-agent ADR 0020).
 */
export const FLEET_BOARD_TAGS = [
  'running',
  'silent',
  'stranded',
  'free',
  'answered',
  'bound',
  'parked',
  'capped',
  'assisted',
  'waiting',
] as const;
export const FleetBoardTagSchema = z.enum(FLEET_BOARD_TAGS);
export type FleetBoardTag = z.infer<typeof FleetBoardTagSchema>;

/** The tags a hold puts on a card — the ones a blocker's row repeats (IMM-1419). */
export const FLEET_BOARD_HOLD_TAGS = ['parked', 'assisted', 'waiting'] as const satisfies readonly FleetBoardTag[];
export const FleetBoardHoldTagSchema = z.enum(FLEET_BOARD_HOLD_TAGS);
export type FleetBoardHoldTag = z.infer<typeof FleetBoardHoldTagSchema>;

/**
 * One open blocker of a card's ticket (IMM-1419): a tracker blocker, or a slice of another ticket the lane's wait named
 * (fleet-agent ADR 0020). A slice has no issue of its own: it shows its parent's title, state, hold and URL.
 */
export const FleetBoardBlockerSchema = z.object({
  /** `IMM-1402`, or a slice ref `IMM-1402.1`. */
  identifier: z.string(),
  title: z.string(),
  /** The blocker's Linear issue; a slice ref's parent's. */
  url: z.url(),
  /** Its tracker state's name; a slice ref's parent's. */
  state: z.string(),
  /** The hold its own card would show; `null` when it is not held, or no shown lane reads its team. */
  tag: FleetBoardHoldTagSchema.nullable(),
  /** A slice ref only: the ticket it belongs to, which the panel opens and links to. `null` for a ticket. */
  parent: z.string().nullable(),
});
export type FleetBoardBlocker = z.infer<typeof FleetBoardBlockerSchema>;

/** The live job holding a ticket. */
export const FleetBoardJobSchema = z.object({
  worker: z.string(),
  /** The holding machine's name. */
  machine: z.string(),
  claimedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  lastBeatAt: z.iso.datetime().nullable(),
  /** The newest write to the round's own task log (fleet-agent ADR 0019); `null` until the holder sends one. */
  logAt: z.iso.datetime().nullable().optional(),
});
export type FleetBoardJob = z.infer<typeof FleetBoardJobSchema>;

/** How often the ticket went round in this lane: runs, parked, picked back up after parking, and the cap. */
export const FleetBoardHistorySchema = z.object({
  runs: z.number().int().min(0),
  parked: z.number().int().min(0),
  resumed: z.number().int().min(0),
  limit: z.number().int().positive(),
});
export type FleetBoardHistory = z.infer<typeof FleetBoardHistorySchema>;

export const FleetBoardCardSchema = z.object({
  identifier: z.string(),
  title: z.string(),
  url: z.url(),
  project: z.string(),
  state: z.string(),
  priority: z.number().int(),
  /** When the ticket entered `state`; `null` when the tracker does not say. */
  stateEnteredAt: z.iso.datetime().nullable(),
  tag: FleetBoardTagSchema,
  /** `running` / `silent` only. */
  job: FleetBoardJobSchema.nullable(),
  /** `bound` only: the one worker that may take it. */
  boundTo: z.string().nullable(),
  /** `parked` only: the first line of the parking round's end detail, when it gave one. */
  reason: z.string().nullable(),
  /** `waiting` only: its open blockers in the tracker, `IMM-123`; empty when the tracker records none. */
  waitingOn: z.array(z.string()),
  /**
   * Every card: its ticket's open tracker blockers in the tracker's order, then — on a ticket carrying the waiting
   * label — the slices of other tickets its wait named. Empty when it has none.
   */
  blockers: z.array(FleetBoardBlockerSchema),
  history: FleetBoardHistorySchema,
  /**
   * The operator's manual place in this lane's queue (`PUT /v1/admin/fleet/board/rank`): an opaque sort key, compared
   * as a plain string. `null` when it has none — it then follows every ranked ticket in Linear's order.
   */
  rank: z.string().nullable(),
});
export type FleetBoardCard = z.infer<typeof FleetBoardCardSchema>;

/** A lane's queues as its workers send them to `jobs/next`. */
export const FleetBoardFlowSchema = z.object({
  states: z.array(z.string()),
  running: z.string().nullable(),
  with: z.array(z.string()),
  without: z.array(z.string()),
});
export type FleetBoardFlow = z.infer<typeof FleetBoardFlowSchema>;

/** One project's part of a lane. */
export const FleetBoardLaneProjectSchema = z.object({
  project: z.string(),
  flow: FleetBoardFlowSchema,
  /** Workers that reported this lane within `FLEET_STALE_AFTER_MS`. */
  workers: z.number().int().min(0),
  maxWorkers: z.number().int().positive().nullable(),
  prFirst: z.boolean(),
  free: z.number().int().min(0),
  running: z.number().int().min(0),
});
export type FleetBoardLaneProject = z.infer<typeof FleetBoardLaneProjectSchema>;

export const FleetBoardLaneSchema = z.object({
  name: z.string(),
  /** In the order the projects first reported; one entry in a project's view. */
  projects: z.array(FleetBoardLaneProjectSchema),
  /** Live jobs, oldest claim first. */
  rounds: z.array(FleetBoardCardSchema),
  stranded: z.array(FleetBoardCardSchema),
  /**
   * In pick order: the first is what this lane's next `jobs/next` gets. Across projects each project's own order is
   * kept and the heads interleave by answered, priority, then age.
   */
  queue: z.array(FleetBoardCardSchema),
  /** In the lane's states, held from it: bound, parked, capped, assisted, waiting. */
  untaken: z.array(FleetBoardCardSchema),
});
export type FleetBoardLane = z.infer<typeof FleetBoardLaneSchema>;

export const GetFleetBoardResponseSchema = z.object({
  /** The view answered: `all`, or a project that has reported lanes. */
  view: z.string(),
  /** `all`, then each project in the order it first reported a lane. */
  views: z.array(z.string()),
  /** The oldest tracker read the board is built on; `null` with no lanes. */
  fetchedAt: z.iso.datetime().nullable(),
  /** In declaration order; in `all`, same-named lanes merged, in the order they first appear. */
  lanes: z.array(FleetBoardLaneSchema),
});
export type GetFleetBoardResponse = z.infer<typeof GetFleetBoardResponseSchema>;

/** A Linear identifier, `IMM-123`: what a rank and a ticket's details are keyed by. */
export const FleetBoardTicketSchema = z.string().regex(/^[A-Z][A-Z0-9]*-[0-9]+$/);

/**
 * `PUT /v1/admin/fleet/board/rank` — place one ticket of a lane's queue right before or right after a neighbour in
 * the same queue (one project's lane: a rank is per project and lane). Both must be in the queue the board shows now,
 * else 409 `fleet.queue.not-in-queue` and the page re-reads the board. Usually one row is written; a ticket dropped
 * below unranked ones ranks those too, so they keep their place above it.
 */
export const RankFleetQueueTicketBodySchema = z
  .object({
    project: FleetProjectNameSchema,
    lane: FleetLaneNameSchema,
    ticket: FleetBoardTicketSchema,
    /** Place it directly above this ticket. */
    before: FleetBoardTicketSchema.optional(),
    /** Place it directly below this ticket. */
    after: FleetBoardTicketSchema.optional(),
  })
  .refine((body) => (body.before === undefined) !== (body.after === undefined), {
    message: 'Exactly one of before and after',
  })
  .refine((body) => body.ticket !== body.before && body.ticket !== body.after, {
    message: 'A ticket is not its own neighbour',
  });
export type RankFleetQueueTicketBody = z.infer<typeof RankFleetQueueTicketBodySchema>;

/** The lane's queue after the move, in pick order. */
export const RankFleetQueueTicketResponseSchema = z.object({ queue: z.array(FleetBoardCardSchema) });
export type RankFleetQueueTicketResponse = z.infer<typeof RankFleetQueueTicketResponseSchema>;

export const FleetBoardTicketPathParamsSchema = z.object({ ticket: FleetBoardTicketSchema });
export type FleetBoardTicketPathParams = z.infer<typeof FleetBoardTicketPathParamsSchema>;

/** One ledger record within a run: a skill step, its model and the tokens it reported (`null` when unparsed). */
export const FleetBoardRunStepSchema = z.object({
  skill: z.string(),
  engine: z.string(),
  model: z.string(),
  startedAt: z.iso.datetime(),
  durationMs: z.number().int().min(0),
  uncachedInputTokens: z.number().int().min(0).nullable(),
  cacheReadInputTokens: z.number().int().min(0).nullable(),
  cacheWriteInputTokens: z.number().int().min(0).nullable(),
  outputTokens: z.number().int().min(0).nullable(),
  totalTokens: z.number().int().min(0).nullable(),
});
export type FleetBoardRunStep = z.infer<typeof FleetBoardRunStepSchema>;

/** A question a run asked, and its answer once one came. */
export const FleetBoardRunQuestionSchema = z.object({
  id: z.uuid(),
  /** The ticket's product TL;DR (OPS-87), shown above the problem; `null` on questions asked without one. */
  brief: z.string().nullable(),
  problem: z.string(),
  options: z.array(FleetQuestionOptionSchema),
  askedAt: z.iso.datetime(),
  answer: z.string().nullable(),
  answeredBy: z.string().nullable(),
  answeredAt: z.iso.datetime().nullable(),
  /** `null` on an unanswered question, and on a legacy operator answer. */
  answererKind: FleetQuestionAnswererKindSchema.nullable(),
  relayedAt: z.iso.datetime().nullable(),
});
export type FleetBoardRunQuestion = z.infer<typeof FleetBoardRunQuestionSchema>;

/** One job row on the ticket (any lane): its holder, how it ended, its ledger steps and its questions. */
export const FleetBoardRunSchema = z.object({
  jobId: z.uuid(),
  key: z.string(),
  /** What follows the ticket in the key; `null` when nothing does. */
  lane: z.string().nullable(),
  worker: z.string(),
  machineId: z.uuid(),
  machineName: z.string(),
  claimEpoch: z.number().int(),
  claimedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  lastBeatAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  /** `null` while the job is live. */
  endReason: FleetJobEndReasonSchema.nullable(),
  /** The end's detail, as the round wrote it. */
  detail: z.string().nullable(),
  /** The newest write to the round's own task log (fleet-agent ADR 0019); `null` until the holder sends one. */
  logAt: z.iso.datetime().nullable().optional(),
  /**
   * Live, with no heartbeat for `FLEET_JOB_STALE_AFTER_MS`, or with its task log unwritten for
   * `FLEET_JOB_LOG_SILENT_AFTER_MS`.
   */
  silent: z.boolean(),
  /** The ledger's records by this worker on this ticket between the claim and the end, oldest first. */
  steps: z.array(FleetBoardRunStepSchema),
  questions: z.array(FleetBoardRunQuestionSchema),
});
export type FleetBoardRun = z.infer<typeof FleetBoardRunSchema>;

/** `GET /v1/admin/fleet/board/tickets/:ticket` — everything the fleet knows about one ticket's runs, newest first. */
export const GetFleetBoardTicketResponseSchema = z.object({
  ticket: z.string(),
  /** At most the newest 50, newest claim first. */
  runs: z.array(FleetBoardRunSchema),
});
export type GetFleetBoardTicketResponse = z.infer<typeof GetFleetBoardTicketResponseSchema>;
